import { useEffect, useRef } from 'react';
import type { ViewKey, ViewPreferenceFor } from '@vagusx/clients';
import {
  useGetViewPreference,
  useSavePreferenceFor,
  useUserPreferences,
} from './use-user-preferences';
import {
  decideHydrateAction,
  decideSaveAction,
  type HydrateAction,
  type SaveAction,
} from './persistent-view-filters-decisions';

// Re-export the pure helpers + types so existing import sites (the hook's
// callers and its test file) don't have to change paths. The decision
// module lives separately so tests can import it without the React +
// api-clients import graph (which requires VITE_API_URL in CI).
export { decideHydrateAction, decideSaveAction, type HydrateAction, type SaveAction };

const SAVE_DEBOUNCE_MS = 500;

// =============================================================================
// Hook
// =============================================================================

interface Args<K extends ViewKey> {
  /** Registered view key — picks which preference slice to read/write. */
  viewKey: K;
  /**
   * Memoized snapshot of just the persistable URL params, in the saved
   * preference shape. Caller is responsible for memoizing on the
   * specific URL primitives so this reference is stable across renders
   * when nothing relevant changed.
   */
  urlSnapshot: ViewPreferenceFor<K>;
  /** True when the URL carries no persistable filter params. */
  urlIsBare: boolean;
  /** Inverse of the URL→preference snapshot. */
  preferenceToUrl: (pref: ViewPreferenceFor<K>) => Record<string, string>;
  /**
   * Optional fresh-user defaults. Applied ONCE on bare-URL mount when the
   * user has never saved this view (savedPref undefined). After the user's
   * first save, this branch never fires again — an explicit "clear all
   * filters" stays cleared across sessions.
   */
  defaultUrlParams?: Record<string, string>;
  /** Implementer must `navigate({ ..., replace: true })` to the same route. */
  onHydrateRedirect: (urlParams: Record<string, string>) => void;
  /**
   * When false, the hook is inert: no hydrate redirect, no save. Used
   * for transient views (e.g. drilldown from dashboard) where the URL
   * is authoritative and we must NOT mutate the user's saved
   * preference. Defaults to true.
   */
  enabled?: boolean;
}

interface Result {
  /**
   * True while the page should hold off on firing data queries that depend
   * on filter values — covers both the prefs-loading window and the brief
   * post-prefs window where the redirect effect is about to fire.
   */
  waitingForHydrate: boolean;
}

/**
 * Generic glue between a page's URL filter state and the user's saved
 * preferences. Page-agnostic; one mechanic, parameterized on view key
 * and converters.
 *
 * Behavior:
 *   - Bare URL + savedPref non-empty   → redirect ONCE to saved filters
 *   - Bare URL + savedPref undefined   → if `defaultUrlParams` set, redirect
 *                                        ONCE to that
 *   - Bare URL + savedPref defined-empty → no redirect (cleared respected)
 *   - URL change → debounce-save the snapshot
 *
 * URL stays the in-session source of truth — this hook bridges it with
 * server-side persistence at the edges of the session.
 */
export function usePersistentViewFilters<K extends ViewKey>(args: Args<K>): Result {
  const {
    viewKey,
    urlSnapshot,
    urlIsBare,
    preferenceToUrl,
    defaultUrlParams,
    onHydrateRedirect,
    enabled = true,
  } = args;
  const { isLoading: prefsLoading, isFetched: prefsFetched } = useUserPreferences();
  const { data: savedPref } = useGetViewPreference(viewKey);
  const savePref = useSavePreferenceFor(viewKey);

  const hasRedirectedRef = useRef(false);
  const lastSavedSnapshotRef = useRef<string | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Refs for callbacks/values so the redirect/save effects don't re-run
  // when the caller passes a fresh function reference each render.
  const onHydrateRedirectRef = useRef(onHydrateRedirect);
  onHydrateRedirectRef.current = onHydrateRedirect;
  const preferenceToUrlRef = useRef(preferenceToUrl);
  preferenceToUrlRef.current = preferenceToUrl;

  const hydrateAction = decideHydrateAction({
    urlIsBare,
    savedPref,
    defaultUrlParams,
    preferenceToUrl,
    enabled,
  });

  // --- One-shot redirect on bare-URL hydrate -----------------------------
  useEffect(() => {
    if (!enabled) return;
    if (!prefsFetched || hasRedirectedRef.current) return;
    hasRedirectedRef.current = true;

    if (hydrateAction.kind === 'redirect-to-saved') {
      lastSavedSnapshotRef.current = hydrateAction.savedSnapshot;
      onHydrateRedirectRef.current(hydrateAction.target);
    } else if (hydrateAction.kind === 'redirect-to-default') {
      onHydrateRedirectRef.current(hydrateAction.target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire-once-after-hydrate
  }, [prefsFetched, enabled]);

  // --- Debounced save on every URL filter change -------------------------
  useEffect(() => {
    // Always clear any pending timer up front so rapid changes collapse to
    // a single fire.
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = undefined;
    }

    if (!enabled) return;
    if (prefsLoading) return;

    const action = decideSaveAction({
      urlSnapshot: urlSnapshot as object,
      lastSavedSnapshot: lastSavedSnapshotRef.current,
      savedPref: savedPref as object | undefined,
      enabled,
    });
    const snapshot = JSON.stringify(urlSnapshot);

    if (action.kind === 'skip') {
      lastSavedSnapshotRef.current = snapshot;
      return;
    }

    saveTimerRef.current = setTimeout(() => {
      lastSavedSnapshotRef.current = snapshot;
      savePref.mutate(urlSnapshot);
    }, SAVE_DEBOUNCE_MS);

    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snapshot + saved values are the meaningful inputs
  }, [urlSnapshot, prefsLoading, savedPref, enabled]);

  // Wait until prefs have loaded AND the redirect (if any) has had a chance
  // to fire. Without the second clause, there's a one-render window after
  // prefs resolve where the page would fire a query with bare-URL filters
  // just to refetch milliseconds later when the redirect lands.
  // When disabled (drilldown mode), the URL is authoritative — no waiting.
  const willRedirect = enabled && hydrateAction.kind !== 'noop';
  const waitingForHydrate =
    enabled && urlIsBare && (prefsLoading || (willRedirect && !hasRedirectedRef.current));

  return { waitingForHydrate };
}
